import _ from "lodash";
import moment from "moment";
import { sha256 } from "js-sha256";
import { Connection, WsClient } from "../WsClient";
import { AbstractService } from "./AbstractService";
import { ConfigurableInterface } from "./ConfigurableInterface";
import { v4 as uuidv4 } from "uuid";

export interface DcmServiceInterface extends ConfigurableInterface {
  getJwt(
    data: { username?: string; password?: string } | null,
    callback: Function,
    errorCallback?: Function
  ): void;
  getClient(uuid: string, callback: Function, errorCallback?: Function): void;
  getClientByName(
    name: string,
    callback: Function,
    errorCallback?: Function
  ): void;
  getProfile(uuid: string, callback: Function, errorCallback?: Function): void;
  getFirmwareSpecList(
    firmwareUuid: string,
    callback: Function,
    errorCallback?: Function
  ): void;
  createSpec(spec: object, callback: Function, errorCallback?: Function): void;
  getFirmwareUuid(
    params: Record<
      "product" | "family" | "model" | "version" | "build",
      string
    >,
    callback: Function,
    errorCallback?: Function
  ): void;
  updateClient(
    client: Record<"uuid" | "firmware_uuid", string>,
    callback: Function,
    errorCallback?: Function
  ): void;
  updateClientProperties(
    clientUuid: string,
    properties: { name: string; type_hash: string; value: unknown }[],
    callback: Function,
    errorCallback?: Function
  ): void;
  sendFeedback(
    data: {
      rate: number;
      expertId: string;
      expertExt: string;
      clientExt: string;
      sessionStart: number;
    },
    callback: Function,
    errorCallback?: Function
  ): void;
  sendAgentServiceVersion(
    version: string,
    callback: Function,
    errorCallback?: Function
  ): void;
}

export class DcmService extends AbstractService implements DcmServiceInterface {
  private maxInt = 2147483647;

  private getConnectionUrl(): string {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort"]);
    return `${this.config.wbdServiceUrl}?port=${this.config.dcmServicePort}`;
  }

  private getWsConnection(): Connection {
    const wsClient = WsClient.getInstance();
    const url = this.getConnectionUrl();

    return wsClient.getConnection(url) ?? wsClient.openConnection(url);
  }

  private scramble(data: string | Array<number>): string {
    if (!data) {
      return "";
    }
    const mask = _.random(0, this.maxInt) % 0xff;
    let scrambled = `0${Number(mask).toString(16)}`.substr(-2);
    for (let i = 0; i < data.length; i++) {
      const ch = typeof data === "string" ? data[i].charCodeAt(0) : data[i];
      scrambled += `0${Number(
        ((ch >> (8 - ((mask + i) % 7))) | (ch << (mask + i) % 7)) % 256
      ).toString(16)}`.substr(-2);
    }
    return scrambled;
  }

  public getJwt(
    data: { username?: string; password?: string } | null,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort"]);

    if (!data?.username) {
      this.checkConfig("clientName");
    }

    if (!data?.password) {
      this.checkConfig("clientPassword");
    }

    const username = data?.username ?? `@${this.config.clientName}`;
    const password = data?.password ?? this.config.clientPassword;

    const timestamp = moment().unix();

    /* eslint-disable */
    // @ts-ignore
    const md5 = require("md5");
    /* eslint-enable */
    const salt = md5("" + _.random(0, this.maxInt));

    const hash = sha256.digest(`${password}${timestamp}${salt}`);

    /* eslint-disable */
    // @ts-ignore
    const ip = require('ip');
    /* eslint-enable */

    const headers = {
      "X-Colibri-Time": "" + timestamp,
      "X-Colibri-Token1": this.scramble(username!),
      "X-Colibri-Token2": this.scramble(salt),
      "X-Colibri-Token3": this.scramble(hash),
      "X-Originating-IP": ip.address()
    };

    const connection = this.getWsConnection();

    connection.sendRequest({
      method: "/client/get_jwt",
      host: this.config.dcmServiceUrl ?? "",
      headers,
      body: [0],
      callback: (jwt: string) => {
        this.disconnect();
        callback(jwt);
      },
      errorCallback: (err: unknown) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }

  public getClient(uuid: string, callback: Function, errorCallback?: Function) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    this.getWsConnection().sendRequest({
      method: "/client/get",
      host: this.config.dcmServiceUrl ?? "",
      jwt: this.config.jwt,
      body: [uuid],
      callback: (data: any) => {
        this.disconnect();
        callback(data);
      },
      errorCallback: (err: any) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }

  public getClientByName(
    name: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    this.getWsConnection().sendRequest({
      method: "/client/get_by_name",
      host: this.config.dcmServiceUrl ?? "",
      jwt: this.config.jwt,
      body: [name],
      callback: (data: any) => {
        this.disconnect();
        callback(data);
      },
      errorCallback: (err: any) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }

  public getProfile(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    this.getWsConnection().sendRequest({
      method: "/client_property/get_profile",
      host: this.config.dcmServiceUrl ?? "",
      jwt: this.config.jwt,
      body: [uuid],
      callback: (data: any) => {
        this.disconnect();
        callback(data);
      },
      errorCallback: (err: any) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }

  public getFirmwareSpecList(
    firmwareUuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    this.getWsConnection().sendRequest({
      method: "/property_spec/get_list_for_firmware",
      host: this.config.dcmServiceUrl!,
      jwt: this.config.jwt,
      body: [firmwareUuid],
      callback: (data: any) => {
        this.disconnect();
        callback(data);
      },
      errorCallback: (err: any) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }

  public getFirmwareUuid(
    params: Record<
      "product" | "family" | "model" | "version" | "build",
      string
    >,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    this.getWsConnection().sendRequest({
      method: "/firmware/get_uuid",
      host: this.config.dcmServiceUrl!,
      jwt: this.config.jwt,
      body: [
        params.product,
        params.family,
        params.model,
        params.version,
        params.build
      ],
      callback: (uuid: string) => {
        this.disconnect();
        callback(uuid);
      },
      errorCallback: (err: any) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }

  public createSpec(
    spec: object,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    this.getWsConnection().sendRequest({
      method: "/specification/create",
      host: this.config.dcmServiceUrl!,
      jwt: this.config.jwt,
      body: [spec],
      callback: (data: any) => {
        this.disconnect();
        callback(data);
      },
      errorCallback: (err: any) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }

  public updateClient(
    client: Record<"uuid" | "firmware_uuid", string>,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    const errCb = (err: any) => {
      this.disconnect();
      errorCallback?.(err);
    };

    this.getWsConnection().sendRequest({
      method: "/locker/lock",
      host: this.config.dcmServiceUrl!,
      jwt: this.config.jwt,
      body: ["client", client.uuid],
      callback: (data: [string, number]) => {
        const [lockUuid] = data;

        this.getWsConnection().sendRequest({
          method: "/client/update",
          host: this.config.dcmServiceUrl!,
          jwt: this.config.jwt,
          body: [lockUuid, client],
          callback: (data: any) =>
            this.getWsConnection().sendRequest({
              method: "/locker/unlock",
              host: this.config.dcmServiceUrl!,
              jwt: this.config.jwt,
              body: [lockUuid],
              callback: (data: any) => {
                this.disconnect();
                callback(data);
              },
              errorCallback: errCb
            }),
          errorCallback: errCb
        });
      },
      errorCallback: errCb
    });
  }

  public updateClientProperties(
    clientUuid: string,
    properties: { name: string; type_hash: string; value: unknown }[],
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig(["wbdServiceUrl", "dcmServicePort", "jwt"]);

    const errCb = (err: any) => {
      this.disconnect();
      errorCallback?.(err);
    };

    this.getWsConnection().sendRequest({
      method: "/locker/lock",
      host: this.config.dcmServiceUrl!,
      jwt: this.config.jwt,
      body: ["client", clientUuid],
      callback: (data: [string, number]) => {
        const [lockUuid] = data;

        const propertiesCopy = _.cloneDeep(properties);

        const cb = () => {
          if (!propertiesCopy.length) {
            this.getWsConnection().sendRequest({
              method: "/locker/unlock",
              host: this.config.dcmServiceUrl!,
              jwt: this.config.jwt,
              body: [lockUuid],
              callback: () => {
                this.getWsConnection().sendRequest({
                  method: "/client/reload",
                  host: this.config.dcmServiceUrl!,
                  jwt: this.config.jwt,
                  body: [clientUuid],
                  callback: (data: any) => {
                    this.disconnect();
                    callback(data);
                  },
                  errorCallback: errCb
                });
              },
              errorCallback: errCb
            });
            return;
          }
          const property = propertiesCopy.shift();
          this.getWsConnection().sendRequest({
            method: "/client_property/set_value",
            host: this.config.dcmServiceUrl!,
            jwt: this.config.jwt,
            body: [lockUuid, clientUuid, property],
            callback: () => cb(),
            errorCallback: errCb
          });
        };

        cb();
      },
      errorCallback: errCb
    });
  }

  public sendFeedback(
    data: {
      rate: number;
      expertId: string;
      expertExt: string;
      clientExt: string;
      sessionStart: number;
    },
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["clientId", "wbdServiceUrl", "dcmServicePort", "jwt"]);

    this.addEventList(
      [
        {
          uuid: `{${uuidv4()}}`,
          // eslint-disable-next-line @typescript-eslint/camelcase
          client_uuid: this.config.clientId!,
          timestamp: +moment() * 1000,
          name: "app.message",
          // eslint-disable-next-line @typescript-eslint/camelcase
          type_hash: "095d71cf12556b9d5e330ad575b3df5d",
          application: "wma",
          facility: "wma",
          priority: 2,
          context: 4,
          payload: JSON.stringify(data)
        }
      ],
      callback,
      errorCallback
    );
  }

  public sendAgentServiceVersion(
    version: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig(["clientId"]);

    this.addEventList(
      [
        {
          uuid: `{${uuidv4()}}`,
          // eslint-disable-next-line @typescript-eslint/camelcase
          client_uuid: this.config.clientId!,
          timestamp: +moment() * 1000,
          name: "app.version",
          application: "REAP",
          facility: "REAP",
          priority: 2,
          context: 4,
          payload: JSON.stringify({
            agentService: version
          })
        }
      ],
      callback,
      errorCallback
    );
  }

  private disconnect() {
    const wsClient = WsClient.getInstance();
    wsClient.closeConnection(this.getConnectionUrl());
  }

  private addEventList(
    events: {
      uuid: string;
      correlation_uuid?: string;
      client_uuid: string;
      timestamp: number;
      name: string;
      type_hash?: string;
      application: string;
      facility: string;
      priority?: number;
      severity?: number;
      context?: number;
      payload?: any;
      comments?: string;
    }[],
    callback: Function,
    errorCallback?: Function
  ) {
    this.getWsConnection().sendRequest({
      method: "/event/add_list",
      host: this.config.dcmServiceUrl!,
      jwt: this.config.jwt,
      body: [events],
      callback: (data: any) => {
        this.disconnect();
        callback(data);
      },
      errorCallback: (err: any) => {
        this.disconnect();
        errorCallback?.(err);
      }
    });
  }
}

export class StagingDcmService extends AbstractService
  implements DcmServiceInterface {
  public getJwt(
    data: { username?: string; password?: string } | null,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback(
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjbGllbnQiOiJ7NWQxNmJlYjItODliNy0xMWVjLWE4YTMtMDI0MmFjMTIwMDAyfSIsInN1YiI6IjEyMzQ1Njc4OTAiLCJpYXQiOjE1MTYyMzkwMjJ9.YtENIkRUmkDq3-TlrwsQkcfatDajsngV_ymDdhygfUs"
        ),
      this.config.stagingRequestDuration
    );
  }

  public getClient(uuid: string, callback: Function, errorCallback?: Function) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          description: "",
          enabled: true,
          // eslint-disable-next-line @typescript-eslint/camelcase
          firmware_uuid: "fake-firmware-uuid",
          icon: "",
          location: "",
          name: "FakeName",
          password: "fake_password",
          // eslint-disable-next-line @typescript-eslint/camelcase
          read_only: false,
          // eslint-disable-next-line @typescript-eslint/camelcase
          role_uuid: "fake-role-uuid",
          template: false,
          // eslint-disable-next-line @typescript-eslint/camelcase
          time_zone: 331,
          uuid
        }),
      this.config.stagingRequestDuration
    );
  }

  public getClientByName(
    name: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback({
          description: "",
          enabled: true,
          // eslint-disable-next-line @typescript-eslint/camelcase
          firmware_uuid: "fake-firmware-uuid",
          icon: "",
          location: "",
          name,
          password: "fake_password",
          // eslint-disable-next-line @typescript-eslint/camelcase
          read_only: false,
          // eslint-disable-next-line @typescript-eslint/camelcase
          role_uuid: "fake-role-uuid",
          template: false,
          // eslint-disable-next-line @typescript-eslint/camelcase
          time_zone: 331,
          uuid: "fake-uuid"
        }),
      this.config.stagingRequestDuration
    );
  }

  public getProfile(
    uuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(
      () =>
        callback([
          {
            name: "webex.email",
            value: "fake-webex-email"
          },
          {
            name: "cisco.sip.extension",
            value: "fake-sip-extension"
          },
          {
            name: "webex.integration.id",
            value: "fake-webex-client-id"
          },
          {
            name: "visual.hint.directory.url",
            value: "/staging/img"
          }
        ]),
      this.config.stagingRequestDuration
    );
  }

  public getFirmwareSpecList(
    firmwareUuid: string,
    callback: Function,
    errorCallback?: Function
  ) {
    setTimeout(() => callback([]), this.config.stagingRequestDuration);
  }

  public getFirmwareUuid(
    params: Record<
      "product" | "family" | "model" | "version" | "build",
      string
    >,
    callback: Function,
    errorCallback?: Function
  ) {
    setTimeout(
      () => callback("{13b3b661-6012-4033-bb75-ca24d93be3d2}"),
      this.config.stagingRequestDuration
    );
  }

  public createSpec(
    spec: object,
    callback: Function,
    errorCallback?: Function
  ) {
    setTimeout(
      () => callback("{98b5cea9-924a-40cc-ba22-53cd51a1d0c7}"),
      this.config.stagingRequestDuration
    );
  }

  public updateClient(
    client: Record<"uuid" | "firmware_uuid", string>,
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback({}), this.config.stagingRequestDuration);
  }

  public updateClientProperties(
    clientUuid: string,
    properties: { name: string; type_hash: string; value: unknown }[],
    callback: Function,
    errorCallback?: Function
  ) {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback({}), this.config.stagingRequestDuration);
  }

  public sendFeedback(
    data: {
      rate: number;
      expertId: string;
      expertExt: string;
      clientExt: string;
      sessionStart: number;
    },
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }

  public sendAgentServiceVersion(
    version: string,
    callback: Function,
    errorCallback?: Function
  ): void {
    this.checkConfig("stagingRequestDuration");

    setTimeout(() => callback(), this.config.stagingRequestDuration);
  }
}
